import React, { useState } from 'react';

const fmt = (n) => Number(n).toLocaleString('en-IN');

const inputStyle = {
  width: '100%', padding: '11px 14px', borderRadius: '8px',
  border: '1px solid var(--border)', backgroundColor: 'var(--bg-secondary)', color: 'var(--text-primary)',
  fontSize: '14px', outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit',
};

const labelStyle = { fontSize: '12px', fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.07em', display: 'block', marginBottom: '6px' };

const severityColor = { high: '#EF4444', medium: '#F59E0B', low: '#10B981' };
const severityBg    = { high: 'rgba(239,68,68,0.08)', medium: 'rgba(245,158,11,0.08)', low: 'rgba(16,185,129,0.08)' };

function downloadAuditPDF(report, form) {
  const { financials: f, legalRedFlags, negotiationScripts, verdict, topAction } = report;
  const flagsHtml = legalRedFlags.map(fl => `
    <div class="flag ${fl.severity}">
      <strong>${fl.flag}</strong> <span class="badge">${fl.severity.toUpperCase()}</span>
      <p>${fl.detail}</p>
      <p class="action">→ ${fl.action}</p>
    </div>`).join('');
  const scriptsHtml = negotiationScripts.map((s, i) => `
    <h3>${i + 1}. ${s.scenario}</h3>
    <div class="script">${s.script}</div>
    ${s.tip ? `<p class="tip">💡 ${s.tip}</p>` : ''}`).join('');

  const html = `
    <h1>Exit Audit Report</h1>
    <p class="sub">Generated by Axigon AI · Exit Engineer</p>
    ${verdict ? `<div class="verdict"><strong>Verdict:</strong> ${verdict}</div>` : ''}
    ${topAction ? `<div class="topaction"><strong>⚡ Do This First:</strong> ${topAction}</div>` : ''}
    <h2>Financial Impact</h2>
    <table><tr>
      <td><div class="metric"><div class="label">Annual Hike</div><div class="value">Rs.${fmt(f.annualHike)}</div></div></td>
      <td><div class="metric"><div class="label">Notice Buyout Cost</div><div class="value">Rs.${fmt(f.noticeBuyoutCost)}</div></div></td>
      <td><div class="metric"><div class="label">Total Exit Cost</div><div class="value">Rs.${fmt(f.totalExitCost)}</div></div></td>
      <td><div class="metric"><div class="label">Net Year-1 Gain</div><div class="value">Rs.${fmt(Math.abs(f.netYear1))}</div></div></td>
    </tr><tr>
      ${f.signonBonus > 0 ? `<td><div class="metric"><div class="label">Sign-on (Net)</div><div class="value">Rs.${fmt(f.signonBonusNet)}</div></div></td>` : ''}
      ${f.recoveryMonths ? `<td><div class="metric"><div class="label">Recovery Timeline</div><div class="value">${f.recoveryMonths} months</div></div></td>` : ''}
    </tr></table>
    <h2>Legal Red Flags</h2>${flagsHtml}
    <h2>Negotiation Scripts</h2>${scriptsHtml}`;

  const w = window.open('', '_blank', 'width=820,height=700');
  w.document.write(`<!DOCTYPE html><html><head><title>Exit Audit — Axigon</title><style>
    body{font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;max-width:700px;margin:40px auto;color:#111;line-height:1.6;font-size:14px}
    h1{font-size:24px;margin:0 0 4px}h2{font-size:14px;color:#555;font-weight:700;margin:24px 0 10px;border-bottom:1px solid #eee;padding-bottom:6px;text-transform:uppercase;letter-spacing:.06em}
    h3{font-size:14px;margin:14px 0 6px}.sub{color:#999;font-size:12px;margin:0 0 20px}
    .verdict{background:#f0fdf4;border-left:3px solid #16a34a;padding:12px 16px;margin:16px 0;border-radius:4px}
    .topaction{background:#fffbeb;border-left:3px solid #d97706;padding:12px 16px;margin:10px 0 20px;border-radius:4px}
    table{width:100%;border-collapse:collapse;margin:10px 0}.metric{padding:10px 8px}.label{font-size:10px;color:#999;text-transform:uppercase;letter-spacing:.06em}.value{font-size:17px;font-weight:700}
    .flag{margin:10px 0;padding:12px 14px;border-left:3px solid #ccc;background:#fafafa;border-radius:0 4px 4px 0}
    .flag.high{border-color:#EF4444;background:#fff5f5}.flag.medium{border-color:#F59E0B;background:#fffbeb}.flag.low{border-color:#10B981;background:#f0fdf4}
    .badge{font-size:10px;font-weight:700;padding:1px 7px;border-radius:3px;background:#eee;margin-left:8px}
    .flag p{margin:4px 0;font-size:13px}.action{font-weight:600}
    .script{background:#f5f5f5;padding:14px;border-radius:6px;white-space:pre-wrap;font-size:13px;line-height:1.7}
    .tip{font-size:12px;color:#888;margin:6px 0 0}
    @media print{button{display:none}}
  </style></head><body>${html}<p style="margin-top:40px;font-size:11px;color:#bbb;border-top:1px solid #eee;padding-top:14px">Generated by Axigon AI · axigon.in · Not legal advice — consult a qualified lawyer for formal matters.</p>
  <button onclick="window.print()" style="position:fixed;top:16px;right:16px;padding:8px 18px;background:#111;color:#fff;border:none;border-radius:6px;cursor:pointer;font-size:13px">Download PDF</button>
  </body></html>`);
  w.document.close();
}

export default function ExitEngineer({ navigateTo, goBack }) {
  const [step, setStep] = useState('form'); // 'form' | 'loading' | 'report'
  const [error, setError] = useState('');
  const [report, setReport] = useState(null);
  const [openScript, setOpenScript] = useState(null);
  const [copied, setCopied] = useState({});

  const [form, setForm] = useState({
    currentCTC: '',
    noticePeriodDays: '90',
    daysServed: '0',
    newCTC: '',
    signonBonus: '',
    bondAmount: '',
    hasNonCompete: false,
    additionalNotes: '',
  });

  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.currentCTC || !form.newCTC) { setError('Current CTC and New CTC are required.'); return; }
    setStep('loading');
    try {
      const res = await fetch('/api/finance?action=exit-audit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          currentCTC:      Number(form.currentCTC.replace(/,/g, '')),
          noticePeriodDays: Number(form.noticePeriodDays),
          daysServed:       Number(form.daysServed || 0),
          newCTC:           Number(form.newCTC.replace(/,/g, '')),
          signonBonus:      form.signonBonus ? Number(form.signonBonus.replace(/,/g, '')) : 0,
          bondAmount:       form.bondAmount   ? Number(form.bondAmount.replace(/,/g, ''))   : 0,
          hasNonCompete:    form.hasNonCompete,
          additionalNotes:  form.additionalNotes,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Audit failed');
      setReport(data);
      setStep('report');
    } catch (err) {
      setError(err.message);
      setStep('form');
    }
  };

  const darkCard = { backgroundColor: 'var(--bg-secondary)', border: '1px solid var(--border)', borderRadius: '14px' };

  // ── FORM ──────────────────────────────────────────────────────────────────
  if (step === 'form') return (
    <section style={{ minHeight: '100vh', backgroundColor: 'var(--bg)', paddingTop: '80px' }}>
      <div style={{ maxWidth: '640px', margin: '0 auto', padding: '40px 24px' }}>
        <button onClick={goBack} style={{ background: 'none', border: 'none', color: 'var(--text-secondary)', fontSize: '13px', cursor: 'pointer', padding: 0, marginBottom: '32px', fontFamily: 'inherit' }}>← Back</button>

        <div style={{ marginBottom: '32px' }}>
          <div style={{ display: 'inline-block', backgroundColor: 'rgba(245,158,11,0.1)', border: '1px solid rgba(245,158,11,0.3)', borderRadius: '6px', padding: '4px 10px', fontSize: '11px', fontWeight: 700, color: '#F59E0B', letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: '14px' }}>Exit Engineer</div>
          <h1 style={{ fontSize: '28px', fontWeight: 700, color: 'var(--text-primary)', margin: '0 0 10px', letterSpacing: '-0.02em' }}>Your Exit Audit</h1>
          <p style={{ fontSize: '14px', color: 'var(--text-secondary)', lineHeight: 1.7, margin: 0 }}>Enter your current and new offer details. We'll calculate your buyout cost, tax impact, and generate negotiation scripts tailored to Indian employment law.</p>
        </div>

        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          {/* Current role */}
          <div style={{ ...darkCard, padding: '24px' }}>
            <p style={{ fontSize: '12px', fontWeight: 700, color: '#F59E0B', textTransform: 'uppercase', letterSpacing: '0.08em', margin: '0 0 18px' }}>Current Role</p>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
              <div>
                <label style={labelStyle}>Current Annual CTC (Rs.)</label>
                <input style={inputStyle} placeholder="e.g. 3200000" value={form.currentCTC} onChange={set('currentCTC')} />
              </div>
              <div>
                <label style={labelStyle}>Notice Period (Days)</label>
                <input style={inputStyle} type="number" min="0" value={form.noticePeriodDays} onChange={set('noticePeriodDays')} />
              </div>
              <div>
                <label style={labelStyle}>Days Already Served</label>
                <input style={inputStyle} type="number" min="0" value={form.daysServed} onChange={set('daysServed')} />
              </div>
              <div>
                <label style={labelStyle}>Employment Bond (Rs.)</label>
                <input style={inputStyle} placeholder="0 if none" value={form.bondAmount} onChange={set('bondAmount')} />
              </div>
            </div>
            <div style={{ marginTop: '14px', display: 'flex', alignItems: 'center', gap: '10px' }}>
              <input type="checkbox" id="noncompete" checked={form.hasNonCompete} onChange={set('hasNonCompete')} style={{ width: '16px', height: '16px', accentColor: '#F59E0B', cursor: 'pointer' }} />
              <label htmlFor="noncompete" style={{ fontSize: '13px', color: 'var(--text-primary)', cursor: 'pointer' }}>My contract has a non-compete clause</label>
            </div>
          </div>

          {/* New offer */}
          <div style={{ ...darkCard, padding: '24px' }}>
            <p style={{ fontSize: '12px', fontWeight: 700, color: '#10B981', textTransform: 'uppercase', letterSpacing: '0.08em', margin: '0 0 18px' }}>New Offer</p>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
              <div>
                <label style={labelStyle}>New Annual CTC (Rs.)</label>
                <input style={inputStyle} placeholder="e.g. 5000000" value={form.newCTC} onChange={set('newCTC')} />
              </div>
              <div>
                <label style={labelStyle}>Sign-on Bonus (Rs.)</label>
                <input style={inputStyle} placeholder="0 if none" value={form.signonBonus} onChange={set('signonBonus')} />
              </div>
            </div>
          </div>

          {/* Notes */}
          <div>
            <label style={labelStyle}>Anything else we should know?</label>
            <textarea
              style={{ ...inputStyle, height: '80px', resize: 'vertical' }}
              placeholder="e.g. They're asking me to serve the full notice. My new company wants me in 30 days."
              value={form.additionalNotes}
              onChange={set('additionalNotes')}
            />
          </div>

          {error && <p style={{ fontSize: '13px', color: '#EF4444', margin: 0 }}>{error}</p>}

          <button type="submit" style={{ width: '100%', padding: '13px', borderRadius: '9px', background: 'var(--text-primary)', color: 'var(--bg)', border: 'none', fontSize: '14px', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>
            Generate Exit Audit →
          </button>
        </form>
      </div>
    </section>
  );

  // ── LOADING ───────────────────────────────────────────────────────────────
  if (step === 'loading') return (
    <section style={{ minHeight: '100vh', backgroundColor: 'var(--bg)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ textAlign: 'center' }}>
        <div style={{ width: '48px', height: '48px', border: '3px solid var(--border)', borderTopColor: '#F59E0B', borderRadius: '50%', animation: 'spin 0.9s linear infinite', margin: '0 auto 20px' }} />
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        <p style={{ color: 'var(--text-secondary)', fontSize: '14px' }}>Analyzing your exit scenario…</p>
      </div>
    </section>
  );

  const copyScript = (text, i) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(prev => ({ ...prev, [i]: true }));
      setTimeout(() => setCopied(prev => ({ ...prev, [i]: false })), 2000);
    });
  };

  // ── REPORT ────────────────────────────────────────────────────────────────
  const { financials: f, legalRedFlags, negotiationScripts, verdict, topAction } = report;
  const hikePositive = f.annualHike > 0;
  const netPositive  = f.netYear1 > 0;

  return (
    <section style={{ minHeight: '100vh', backgroundColor: 'var(--bg)', paddingTop: '80px' }}>
      <div style={{ maxWidth: '800px', margin: '0 auto', padding: '40px 24px' }}>

        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '32px', flexWrap: 'wrap', gap: '12px' }}>
          <div>
            <div style={{ display: 'inline-block', backgroundColor: 'rgba(245,158,11,0.1)', border: '1px solid rgba(245,158,11,0.3)', borderRadius: '6px', padding: '4px 10px', fontSize: '11px', fontWeight: 700, color: '#F59E0B', letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: '10px' }}>Exit Audit Report</div>
            <h1 style={{ fontSize: '26px', fontWeight: 700, color: 'var(--text-primary)', margin: 0, letterSpacing: '-0.02em' }}>Your Exit Analysis</h1>
          </div>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            <button onClick={() => downloadAuditPDF(report, form)} style={{ background: '#F59E0B', color: 'var(--bg)', border: 'none', borderRadius: '8px', padding: '8px 16px', fontSize: '13px', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>Download Report ↓</button>
            <button onClick={() => setStep('form')} style={{ background: 'transparent', border: '1px solid var(--border)', color: 'var(--text-secondary)', borderRadius: '8px', padding: '8px 16px', fontSize: '13px', cursor: 'pointer', fontFamily: 'inherit' }}>Recalculate</button>
          </div>
        </div>

        {/* Verdict banner */}
        {verdict && (
          <div style={{ ...darkCard, padding: '20px 24px', marginBottom: '20px', borderLeft: `3px solid ${netPositive ? '#10B981' : '#EF4444'}` }}>
            <p style={{ fontSize: '11px', fontWeight: 700, color: netPositive ? '#10B981' : '#EF4444', textTransform: 'uppercase', letterSpacing: '0.08em', margin: '0 0 8px' }}>Verdict</p>
            <p style={{ fontSize: '14px', color: 'var(--text-primary)', lineHeight: 1.7, margin: 0 }}>{verdict}</p>
          </div>
        )}

        {/* Top action */}
        {topAction && (
          <div style={{ backgroundColor: 'rgba(245,158,11,0.06)', border: '1px solid rgba(245,158,11,0.25)', borderRadius: '10px', padding: '16px 20px', marginBottom: '28px', display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
            <span style={{ fontSize: '16px', flexShrink: 0 }}>⚡</span>
            <div>
              <p style={{ fontSize: '11px', fontWeight: 700, color: '#F59E0B', textTransform: 'uppercase', letterSpacing: '0.08em', margin: '0 0 4px' }}>Do This First</p>
              <p style={{ fontSize: '14px', color: 'var(--text-primary)', lineHeight: 1.6, margin: 0 }}>{topAction}</p>
            </div>
          </div>
        )}

        {/* ── SECTION 1: Financial Impact ────────────────────────────────── */}
        <div style={{ marginBottom: '28px' }}>
          <p style={{ fontSize: '11px', fontWeight: 700, color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.1em', margin: '0 0 14px' }}>Financial Impact</p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px' }}>
            {[
              { label: 'Annual Hike',         value: `Rs.${fmt(f.annualHike)}`,        sub: `Rs.${fmt(f.monthlyHike)}/month`,          color: hikePositive ? '#10B981' : '#EF4444' },
              { label: 'Notice Buyout Cost',  value: `Rs.${fmt(f.noticeBuyoutCost)}`,  sub: `${f.remainingDays} days × Rs.${fmt(f.dailyRate)}/day`, color: '#EF4444' },
              { label: 'Bond Recovery',       value: f.bondCost > 0 ? `Rs.${fmt(f.bondCost)}` : '—',  sub: f.bondCost > 0 ? 'Check enforceability' : 'None',  color: f.bondCost > 0 ? '#F59E0B' : 'var(--text-tertiary)' },
              { label: 'Sign-on Bonus (Net)', value: f.signonBonus > 0 ? `Rs.${fmt(f.signonBonusNet)}` : '—', sub: f.signonBonus > 0 ? `Rs.${fmt(f.signonBonusTax)} tax (${f.marginalRatePct}%)` : 'None', color: f.signonBonus > 0 ? '#10B981' : 'var(--text-tertiary)' },
              { label: 'Total Exit Cost',     value: `Rs.${fmt(f.totalExitCost)}`,     sub: f.recoveryMonths ? `Recovered in ${f.recoveryMonths} months` : 'No hike to recover',  color: '#F59E0B' },
              { label: 'Net Year-1 Gain',     value: `Rs.${fmt(Math.abs(f.netYear1))}`, sub: netPositive ? 'In your favour' : 'Net loss in year 1', color: netPositive ? '#10B981' : '#EF4444' },
            ].map(({ label, value, sub, color }) => (
              <div key={label} style={{ ...darkCard, padding: '18px 20px' }}>
                <p style={{ fontSize: '11px', color: 'var(--text-tertiary)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.07em', margin: '0 0 8px' }}>{label}</p>
                <p style={{ fontSize: '20px', fontWeight: 700, color, margin: '0 0 4px', letterSpacing: '-0.01em' }}>{value}</p>
                <p style={{ fontSize: '12px', color: 'var(--text-secondary)', margin: 0 }}>{sub}</p>
              </div>
            ))}
          </div>
        </div>

        {/* ── SECTION 2: Legal Red Flags ─────────────────────────────────── */}
        {legalRedFlags.length > 0 && (
          <div style={{ marginBottom: '28px' }}>
            <p style={{ fontSize: '11px', fontWeight: 700, color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.1em', margin: '0 0 14px' }}>Legal Red Flags</p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {legalRedFlags.map((flag, i) => (
                <div key={i} style={{ ...darkCard, padding: '18px 20px', borderLeft: `3px solid ${severityColor[flag.severity] || 'var(--text-secondary)'}`, backgroundColor: severityBg[flag.severity] || 'var(--bg-secondary)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
                    <span style={{ fontSize: '11px', fontWeight: 700, color: severityColor[flag.severity] || 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.08em', border: `1px solid ${severityColor[flag.severity]}40`, borderRadius: '4px', padding: '2px 8px' }}>{flag.severity}</span>
                    <p style={{ fontSize: '14px', fontWeight: 600, color: 'var(--text-primary)', margin: 0 }}>{flag.flag}</p>
                  </div>
                  <p style={{ fontSize: '13px', color: 'var(--text-secondary)', lineHeight: 1.65, margin: '0 0 10px' }}>{flag.detail}</p>
                  <p style={{ fontSize: '12px', color: severityColor[flag.severity] || 'var(--text-secondary)', fontWeight: 600, margin: 0 }}>→ {flag.action}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── SECTION 3: Negotiation Scripts ─────────────────────────────── */}
        {negotiationScripts.length > 0 && (
          <div style={{ marginBottom: '40px' }}>
            <p style={{ fontSize: '11px', fontWeight: 700, color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.1em', margin: '0 0 14px' }}>Negotiation Scripts</p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {negotiationScripts.map((s, i) => (
                <div key={i} style={{ ...darkCard, overflow: 'hidden' }}>
                  <button
                    onClick={() => setOpenScript(openScript === i ? null : i)}
                    style={{ width: '100%', background: 'none', border: 'none', padding: '18px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', fontFamily: 'inherit', textAlign: 'left' }}
                  >
                    <span style={{ fontSize: '14px', fontWeight: 600, color: 'var(--text-primary)' }}>{s.scenario}</span>
                    <span style={{ color: 'var(--text-tertiary)', fontSize: '16px', flexShrink: 0 }}>{openScript === i ? '−' : '+'}</span>
                  </button>
                  {openScript === i && (
                    <div style={{ padding: '0 20px 20px' }}>
                      <div style={{ backgroundColor: 'var(--bg-secondary)', border: '1px solid var(--border)', borderRadius: '8px', padding: '14px 16px', marginBottom: '12px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
                          <p style={{ fontSize: '11px', fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.07em', margin: 0 }}>Script</p>
                          <button
                            onClick={() => copyScript(s.script, i)}
                            style={{ background: copied[i] ? 'rgba(16,185,129,0.15)' : 'rgba(245,158,11,0.1)', border: `1px solid ${copied[i] ? '#10B981' : 'rgba(245,158,11,0.3)'}`, borderRadius: '5px', color: copied[i] ? '#10B981' : '#F59E0B', fontSize: '11px', fontWeight: 700, padding: '3px 10px', cursor: 'pointer', fontFamily: 'inherit', transition: 'all 0.2s' }}
                          >{copied[i] ? '✓ Copied' : 'Copy'}</button>
                        </div>
                        <p style={{ fontSize: '13px', color: 'var(--text-primary)', lineHeight: 1.75, margin: 0, whiteSpace: 'pre-wrap' }}>{s.script}</p>
                      </div>
                      {s.tip && (
                        <p style={{ fontSize: '12px', color: '#F59E0B', lineHeight: 1.6, margin: 0 }}>💡 {s.tip}</p>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        <button onClick={goBack} style={{ background: 'transparent', border: '1px solid var(--border)', color: 'var(--text-secondary)', borderRadius: '8px', padding: '10px 20px', fontSize: '13px', cursor: 'pointer', fontFamily: 'inherit' }}>← Back to Dashboard</button>
      </div>
    </section>
  );
}
